import React from 'react';
import { useTheme } from 'styled-components';
import { Link } from 'react-router-dom';
import { BasicContainer, FlexRow, ImageButton } from '@minecraft.atoms';
import { DesignSystemIconColor } from '@minecraft.themes';
import Icon from '@minecraft.icon';
import UncontrolledTooltip from '@minecraft.uncontrolled-tooltip';

export const SPICERACK_ICONS = {
  location: 'spicerack-location',
  reel: 'spicerack-reel',
  resume: 'spicerack-resume',
  overscale: 'spicerack-overscale',
  'audition-history': 'spicerack-audition-history',
  'submission-media': 'submission-media',
  'submission-note': 'submission-note',
  // while these permanent note icons have different
  // colors built into them by default
  // they do have unique designs for each status :(
  // so they all get their own icon
  'permanent-note-inactive': 'permanent-note-grey',
  'permanent-note-default': 'permanent-note-black',
  'permanent-note-very-good': 'permanent-note-green',
  'permanent-note-bad': 'permanent-note-red',
  'permanent-note-good': 'permanent-note-orange',
  'project-note': 'project-note',
} as const;

export type SpicerackItemIcon = keyof typeof SPICERACK_ICONS;

interface SpicerackItemBaseProps {
  // a unique id for this spicerack's baseball card, usually a profile id or submission id
  id: string;
  // a unique identifier for this item in the spicerack, used for differentiating each
  // element in the spicerack from each other
  code: string;
  // whether or not the item is disabled, when disabled the item (link or button) will not be clickable
  // and will be set to the "disabled" color, even if the color prop is set
  disabled?: boolean;
  // the label for the item for a11y purposes
  label: string;
  // the name of the icon to display for the item
  iconName: SpicerackItemIcon;
  // a custom color to use for the icon
  // usually to indicate a special status for the item
  iconColor?: DesignSystemIconColor;
  // a tooltip to display when hovering over the item
  // this is usually provided when the item is not disabled to give some context
  // for what its purpose is
  tooltip?: React.ReactNode;
  // a qa-id for the item
  'data-qa-id'?: string;
  // a test-id for the item
  'data-testid'?: string;
}

const SpicerackIcon = ({
  iconName: iconNameProp,
  iconColor,
  disabled,
}: Pick<SpicerackItemBaseProps, 'iconName' | 'iconColor' | 'disabled'>) => {
  const theme = useTheme();
  const iconColorName = disabled ? 'inactive' : iconColor;
  const iconThemeColor = theme.designSystem.iconColors[iconColorName ?? 'active'];
  const iconName = SPICERACK_ICONS[iconNameProp];

  if (!iconName) {
    return null;
  }

  return <Icon size="spicerack" name={iconName} color={iconThemeColor} />;
};

interface SpicerackLinkProps extends SpicerackItemBaseProps {
  // tells the spicerack to render this item as a link
  type: 'link';
  // what url to navigate to when the link is clicked
  href: string;
}

const SpicerackLinkItem = ({
  'data-qa-id': dataQaId,
  'data-testid': dataTestId,
  code,
  disabled,
  href,
  iconColor,
  iconName,
  id,
  label,
  tooltip,
  type,
}: SpicerackLinkProps) => {
  const linkId = `${code}-${id}`;

  if (type !== 'link') {
    return null;
  }

  return (
    <Link
      aria-label={label}
      data-qa-id={`${dataQaId}-${code}`}
      data-testid={dataTestId}
      disabled={disabled || !href}
      id={linkId}
      target="_blank"
      to={href}
    >
      <SpicerackIcon iconColor={iconColor} iconName={iconName} disabled={disabled} />
      {!disabled && tooltip && (
        <UncontrolledTooltip placement="top" target={linkId} data-testid={`${linkId}-tooltip`}>
          {tooltip}
        </UncontrolledTooltip>
      )}
    </Link>
  );
};

interface SpicerackButtonProps extends SpicerackItemBaseProps {
  type: 'button';
  onClick: (e: React.MouseEvent) => void;
}

const SpicerackButtonItem = ({
  'data-qa-id': dataQaId,
  'data-testid': dataTestId,
  code,
  disabled,
  iconColor,
  iconName,
  id,
  label,
  onClick,
  tooltip,
  type,
}: SpicerackButtonProps) => {
  const buttonId = `${code}-${id}`;

  if (type !== 'button') {
    return null;
  }

  return (
    <ImageButton
      aria-label={label}
      data-qa-id={`${dataQaId}-${code}`}
      data-testid={dataTestId}
      disabled={disabled}
      id={buttonId}
      onClick={onClick}
      type="button"
      className="cn_m-0 cn_atom_p-0"
    >
      <SpicerackIcon iconColor={iconColor} iconName={iconName} disabled={disabled} />
      {!disabled && tooltip && (
        <UncontrolledTooltip placement="top" target={buttonId} data-testid={`${buttonId}-tooltip`}>
          {tooltip}
        </UncontrolledTooltip>
      )}
    </ImageButton>
  );
};

interface SpicerackDividerProps {
  type: 'divider';
  // a unique value for this in the spicerack
  // to use as a key
  code: string;
}

const SpicerackDividerItem = ({ type, code }: SpicerackDividerProps) => {
  if (type !== 'divider') {
    return null;
  }

  return (
    <BasicContainer
      borderPlacement="left"
      borderSize={0.07}
      id={`spicerack-item-divider-${code}`}
      data-testid={`spicerack-item-divider-${code}`}
      className="cn_h-full"
    />
  );
};

type SpicerackItemProps = SpicerackLinkProps | SpicerackButtonProps | SpicerackDividerProps;

// the spicerack injects id into each item to ensure that tooltips are uniquely identified
// so the props that are passed to the spicerack do not need to include that value
type SpicerackItemConfig = Omit<SpicerackItemProps, 'id'>;

const isSpicerackLinkItem = (item: SpicerackItemConfig): item is SpicerackLinkProps => {
  return item?.type === 'link';
};

const isSpicerackButtonItem = (item: SpicerackItemConfig): item is SpicerackButtonProps => {
  return item?.type === 'button';
};

const isSpicerackDividerItem = (item: SpicerackItemConfig): item is SpicerackDividerProps => {
  return item?.type === 'divider';
};

export interface SpicerackProps {
  // a unique id for this spicerack's baseball card, usually a profile id or submission id
  // this needs to be unique for each baseball card so that tooltips can be uniquely identified
  // to their target
  id: string | number;
  // a list of the items to display in the spicerack
  // each item can be a link or button with a label, icon, color, and tooltip
  items: SpicerackItemConfig[];
  // qa id for the spicerack overall
  'data-qa-id'?: string;
  /**
   * whether to round the left and right sides of the spicerack
   * use this when the spicerack is floating and not contained in a card
   * like on the casting project worksheet, or casting voice over submission list item
   */
  rounded?: boolean;
  /**
   * negativeMargin - adds 0.5rem of negative left and right margin to the spicerack
   * @deprecated do not use this in new implementations,
   * it is for compatability with old baseball card wrappers
   * that have padding on the parent element where the spicerack
   * needs to go to the edge of the card
   */
  negativeMargin?: boolean;
}

export const Spicerack = ({
  'data-qa-id': dataQaId,
  id,
  items = [],
  negativeMargin = false,
  rounded = false,
}: SpicerackProps) => {
  const className = negativeMargin ? 'cn_mx-2-negative' : 'cn_w-full';
  return (
    <FlexRow
      alignItems="center"
      borderRadius={rounded ? '2xl' : 'none'}
      bgColor="spicerack"
      className={'cn_h-8 cn_atom_px-4'.concat(' ', className)}
      data-qa-id={dataQaId}
      data-testid="spicerack-container"
      gap="1"
      grow={0}
      justifyContent="space-evenly"
      wrap="nowrap"
    >
      {(Array.isArray(items) ? items : []).map((config) => {
        if (isSpicerackLinkItem(config)) {
          return <SpicerackLinkItem id={id?.toString()} data-qa-id={dataQaId} key={config?.code} {...config} />;
        }

        if (isSpicerackButtonItem(config)) {
          return <SpicerackButtonItem id={id?.toString()} data-qa-id={dataQaId} key={config?.code} {...config} />;
        }

        if (isSpicerackDividerItem(config)) {
          return <SpicerackDividerItem key={config?.code} {...config} />;
        }

        return null;
      })}
    </FlexRow>
  );
};
